import { supabase } from '../lib/supabase';

class TopicService {
  // Create new topic
  async createTopic(topicData) {
    try {
      console.log('TopicService: Creating topic with data:', topicData);
      const { data, error } = await supabase?.schema('public')?.from('topics')?.insert([topicData])?.select()?.single();
      console.log('TopicService: Insert result:', { data, error });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('TopicService: Error creating topic:', error);
      return { data: null, error };
    }
  }

  // Get all topics for a user (alias for backward compatibility)
  async getTopics(userId) {
    return this.getUserTopics(userId);
  }

  // Get all topics for a user
  async getUserTopics(userId) {
    try {
      console.log(`🔍 [getUserTopics] Loading topics for user: ${userId}`);
      
      // Simple select without invalid relationships
      // Topics table is in public schema, not deepsearch
      const { data, error } = await supabase
        .schema('public')
        .from('topics')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ [getUserTopics] Error loading topics:', error);
        throw error;
      }

      console.log(`✅ [getUserTopics] Loaded ${data?.length || 0} topics`);

      // If no topics, return early
      if (!data || data.length === 0) {
        return { data: [], error: null };
      }

      // Get counts for ALL topics via RPC (server-aggregated, no N+1)
      const topicIds = data.map(t => t.id);
      const { data: countRows, error: countError } = await supabase
        .schema('public')
        .rpc('topic_item_counts', { topic_ids: topicIds });

      if (countError) {
        console.warn(`⚠️ [getUserTopics] Error counting content:`, countError);
      }

      // Build count dictionary
      const countMap = {};
      (countRows || []).forEach(row => {
        countMap[row.topic_id] = Number(row.content_count || 0);
      });

      // Merge counts into topics
      const topicsWithCount = data.map(topic => ({
        ...topic,
        content_count: countMap[topic.id] || 0,
        conversation_count: 0, // Topics don't have conversations - folders do
      }));

      return { data: topicsWithCount, error: null };
    } catch (error) {
      console.error('❌ [getUserTopics] Error:', error);
      return { data: null, error };
    }
  }

  // Get topic by ID with content
  async getTopicWithContent(topicId) {
    try {
      console.log(`🔍 [getTopicWithContent] Loading topic ${topicId}`);
      
      // Load topic first
      const { data: topic, error: topicError } = await supabase
        .schema('public')
        .from('topics')
        .select('*')
        .eq('id', topicId)
        .single();

      if (topicError) {
        console.error('❌ [getTopicWithContent] Error loading topic:', topicError);
        throw topicError;
      }

      // Load content separately
      const { data: content, error: contentError } = await supabase
        .schema('public')
        .from('instagram_content')
        .select('*')
        .eq('topic_id', topicId);

      if (contentError) {
        console.warn('⚠️ [getTopicWithContent] Error loading content:', contentError);
      }

      return { 
        data: { 
          ...topic, 
          content: content || [] 
        }, 
        error: null 
      };
    } catch (error) {
      console.error('❌ [getTopicWithContent] Error:', error);
      return { data: null, error };
    }
  }

  // Update topic
  async updateTopic(topicId, updates) {
    try {
      const { data, error } = await supabase?.schema('public')?.from('topics')?.update(updates)?.eq('id', topicId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Delete topic
  async deleteTopic(topicId) {
    try {
      const { error } = await supabase?.schema('public')?.from('topics')?.delete()?.eq('id', topicId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  // Find topic by name
  async findTopicByName(userId, topicName) {
    try {
      const { data, error } = await supabase?.schema('public')?.from('topics')?.select('*')?.eq('user_id', userId)?.ilike('name', topicName)?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned"
      return { data: error ? null : data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Ensure "All" collection exists for user (special collection that contains everything)
  async ensureAllCollection(userId) {
    try {
      // Check if "All" collection exists
      const { data: existingAll } = await this.findTopicByName(userId, 'All');
      
      if (existingAll) {
        return { data: existingAll, error: null };
      }

      // Create "All" collection
      const allData = {
        user_id: userId,
        name: 'All',
        description: 'All your content in one place',
        color: '#3B82F6' // Blue color for "All"
      };

      return await this.createTopic(allData);
    } catch (error) {
      return { data: null, error };
    }
  }

  // Ensure Misc topic exists for user
  async ensureMiscTopic(userId) {
    try {
      // Check if Misc topic exists
      const { data: existingMisc } = await this.findTopicByName(userId, 'Misc');
      
      if (existingMisc) {
        return { data: existingMisc, error: null };
      }

      // Create Misc topic
      const miscData = {
        user_id: userId,
        name: 'Misc',
        description: 'Files that couldn\'t be automatically categorized',
        color: '#6B7280' // Gray color for misc
      };

      return await this.createTopic(miscData);
    } catch (error) {
      return { data: null, error };
    }
  }

  // Get or create topic by name (for AI categorization)
  async getOrCreateTopic(userId, topicName, suggestedColor = null) {
    try {
      // Try to find existing topic
      const { data: existingTopic } = await this.findTopicByName(userId, topicName);
      
      if (existingTopic) {
        return { data: existingTopic, error: null };
      }

      // Create new topic
      const topicData = {
        user_id: userId,
        name: topicName,
        description: `Auto-created topic for ${topicName} content`,
        color: suggestedColor || this.getRandomColor()
      };

      return await this.createTopic(topicData);
    } catch (error) {
      return { data: null, error };
    }
  }

  // Generate random color for new topics
  getRandomColor() {
    const colors = [
      '#3B82F6', // Blue
      '#10B981', // Emerald
      '#F59E0B', // Amber
      '#EF4444', // Red
      '#8B5CF6', // Violet
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#F97316', // Orange
      '#EC4899', // Pink
      '#14B8A6'  // Teal
    ];
    return colors?.[Math.floor(Math.random() * colors?.length)];
  }

  // Real-time subscription for topic changes
  subscribeToTopicChanges(userId, callback) {
    const subscription = supabase?.channel('topics_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'topics',
          filter: `user_id=eq.${userId}`
        },
        callback
      )?.subscribe();

    return subscription;
  }
}

export default new TopicService();